/**
 * Pacote 2 · P2-03/P2-04 (D7) — "Colunas" e "Visão" das listas de consulta, por usuário e por lista, guardadas
 * no servidor (GET/PUT /api/preferencias…); nada no armazenamento do navegador.
 *  - Colunas: lidas dos títulos da própria tabela; a primeira coluna e "Ações" são fixas; mostrar/ocultar e
 *    reordenar (arrastar ⋮⋮ ou ↑ ↓) com Aplicar (padrão A) e "Restaurar padrão". A ordem é aplicada movendo as
 *    células de cada linha (as fixas ficam no lugar; linhas com colSpan não mudam) e a ocultação marca as células
 *    (data-oculta), sem mexer no conteúdo nem nos dados.
 *  - Visão: filtros (formato próprio da tela) + ordenação + colunas; salvar, atualizar, renomear, excluir e
 *    definir a padrão (aplicada ao abrir a lista). Os dados continuam limitados pelas permissões do usuário.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { api } from '../api.ts';
import { Icone } from '../componentes/icones.tsx';
import { ehFixa, ordemParaGravar, posicoesFinais } from './ordemColunas.ts';
import type { EstadoVisao, ListaPersonalizavel, PreferenciasLista, Visao } from '../../../backend/src/compartilhado/listas.ts';

type Ordem = { coluna: string; direcao: 'asc' | 'desc'; definir: (c: never, d: 'asc' | 'desc') => void };
export interface OpcoesPersonalizacao<F> {
  /** busca textual da lista: a visão guarda e reaplica o texto (revisão 12/10) */
  busca?: { valor: string; definir: (v: string) => void };
  /** estado atual dos filtros da tela (serializável) */
  filtros?: F;
  /** aplica os filtros de uma visão */
  aplicarFiltros?: (f: F) => void;
  /** ordenação da tabela (useOrdenacao) */
  ordenacao?: Ordem;
  /** a lista só tem visões (Kanban) */
  semColunas?: boolean;
  /** só colunas (a visão da tela fica em outra lista, ex.: OL/OTI dentro de Ordens) */
  semVisoes?: boolean;
  /** ao abrir, a visão padrão aplica só as colunas: busca, filtros e ordenação ficam como estão (ex.: guardados na sessão) */
  manterFiltrosAoAbrir?: boolean;
}

const textoCabecalho = (th: Element) => (th.querySelector('.rotulo-coluna')?.textContent ?? th.textContent ?? '').replace(/[▲▼↕]/g, '').trim();

/**
 * Reordena as colunas não fixas conforme `ordem` (nomes); as que não constam seguem na ordem original. Cada célula
 * guarda a posição original (data-col) na primeira vez em que é vista; só move quando a ordem difere (idempotente,
 * para não realimentar o MutationObserver).
 */
function aplicarOrdem(tabela: HTMLTableElement | null, ordem: string[]) {
  const cab = tabela?.tHead?.rows[0];
  if (!tabela || !cab) return;
  const n = cab.cells.length;
  const linhas = [...tabela.rows].filter((r) => r.cells.length === n && [...r.cells].every((c) => c.colSpan === 1));
  for (const r of linhas) [...r.cells].forEach((c, i) => c.dataset.col ?? (c.dataset.col = String(i)));
  const originais = [...cab.cells].sort((a, b) => Number(a.dataset.col) - Number(b.dataset.col));
  const nomes = originais.map(textoCabecalho);
  const final = posicoesFinais(nomes, ordem);
  for (const r of linhas) {
    const atual = [...r.cells].map((c) => Number(c.dataset.col));
    if (atual.every((x, i) => x === final[i])) continue;
    const porCol = new Map([...r.cells].map((c) => [Number(c.dataset.col), c]));
    for (const col of final) r.appendChild(porCol.get(col)!);
  }
}

/** Esconde, linha a linha, as células das colunas ocultas (respeitando colSpan); linhas de detalhe não mudam. */
function aplicarOcultas(tabela: HTMLTableElement | null, ocultas: string[]) {
  if (!tabela) return;
  const cab = tabela.tHead?.rows[0];
  if (!cab) return;
  const nomes = [...cab.cells].map(textoCabecalho);
  const indices = new Set(nomes.map((n, i) => (!ehFixa(n, i, nomes) && ocultas.includes(n) ? i : -1)).filter((i) => i >= 0));
  for (const linha of [...tabela.rows]) {
    let pos = 0;
    for (const cel of [...linha.cells]) {
      const ocultar = cel.colSpan === 1 && indices.has(pos);
      if (ocultar) cel.setAttribute('data-oculta', '');
      else cel.removeAttribute('data-oculta');
      pos += cel.colSpan;
    }
  }
}

export function usePersonalizacao<F extends object>(lista: ListaPersonalizavel | null, opcoes: OpcoesPersonalizacao<F> = {}) {
  const tabela = useRef<HTMLTableElement>(null);
  const [pref, setPref] = useState<PreferenciasLista | null>(null);
  const [ocultasVisao, setOcultasVisao] = useState<string[] | null>(null);
  const [ordemVisao, setOrdemVisao] = useState<string[] | null>(null);
  const [ativa, setAtiva] = useState<string | null>(null);
  const [erro, setErro] = useState('');
  const aplicouPadrao = useRef(false);
  const op = useRef(opcoes);
  op.current = opcoes;

  const aplicarVisao = useCallback((v: Visao) => {
    const e = v.estado;
    if (op.current.busca) op.current.busca.definir(e.busca ?? '');
    if (e.filtros && op.current.aplicarFiltros) op.current.aplicarFiltros(e.filtros as F);
    if (e.ordenacao && op.current.ordenacao) op.current.ordenacao.definir(e.ordenacao.coluna as never, e.ordenacao.direcao);
    setOcultasVisao(e.colunasOcultas ?? []);
    setOrdemVisao(e.ordemColunas ?? []);
    setAtiva(v.id);
  }, []);

  useEffect(() => {
    let vivo = true;
    aplicouPadrao.current = false;
    if (!lista) return;
    api
      .get<PreferenciasLista>(`/api/preferencias/listas/${lista}`)
      .then((p) => {
        if (!vivo) return;
        setPref(p);
        const padrao = p.visoes.find((v) => v.padrao);
        if (padrao && !aplicouPadrao.current) {
          aplicouPadrao.current = true;
          if (op.current.manterFiltrosAoAbrir) {
            setOcultasVisao(padrao.estado.colunasOcultas ?? []);
            setOrdemVisao(padrao.estado.ordemColunas ?? []);
          } else aplicarVisao(padrao);
        }
      })
      .catch((e: Error) => vivo && setErro(e.message));
    return () => {
      vivo = false;
    };
  }, [lista, aplicarVisao]);

  const ocultas = ocultasVisao ?? pref?.colunasOcultas ?? [];
  const ordem = ordemVisao ?? pref?.ordemColunas ?? [];
  const chave = JSON.stringify([ocultas, ordem]);
  // depois de cada render, e quando as linhas mudam, aplica a ordem e marca as células das colunas ocultas
  const aplicar = (t: HTMLTableElement | null, o: string[], h: string[]) => (aplicarOrdem(t, o), aplicarOcultas(t, h));
  useLayoutEffect(() => aplicar(tabela.current, ordem, ocultas));
  useEffect(() => {
    const t = tabela.current;
    if (!t) return;
    const [h, o] = JSON.parse(chave) as [string[], string[]];
    const obs = new MutationObserver(() => aplicar(t, o, h));
    obs.observe(t, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [chave]);

  const estadoAtual = (): EstadoVisao => ({
    busca: opcoes.busca?.valor.trim() ?? '',
    filtros: (opcoes.filtros ?? {}) as Record<string, unknown>,
    ordenacao: opcoes.ordenacao ? { coluna: opcoes.ordenacao.coluna, direcao: opcoes.ordenacao.direcao } : null,
    colunasOcultas: ocultas,
    ordemColunas: ordem,
  });
  const executar = async (f: () => Promise<PreferenciasLista>) => {
    setErro('');
    try {
      const p = await f();
      setPref(p);
      return p;
    } catch (e) {
      setErro((e as Error).message);
      return null;
    }
  };
  const salvarColunas = (cols: string[], ordemNova: string[]) =>
    executar(async () => {
      const p = await api.put<PreferenciasLista>(`/api/preferencias/listas/${lista}/colunas`, { colunasOcultas: cols, ordemColunas: ordemNova });
      setOcultasVisao(null);
      setOrdemVisao(null);
      return p;
    });

  const controles = (
    <span className="controles-personalizacao" data-teste={`personalizacao-${lista}`}>
      {!opcoes.semVisoes && (
        <SeletorVisoes
          visoes={pref?.visoes ?? []}
          ativa={ativa}
          erro={erro}
          aoAplicar={aplicarVisao}
          aoSalvarNova={(nome, padrao) => executar(() => api.post<PreferenciasLista>(`/api/preferencias/listas/${lista}/visoes`, { nome, padrao, estado: estadoAtual() })).then((p) => p && setAtiva(p.visoes.find((v) => v.nome === nome.trim())?.id ?? null))}
          aoAtualizar={(v) => void executar(() => api.patch<PreferenciasLista>(`/api/preferencias/visoes/${v.id}`, { estado: estadoAtual() }))}
          aoRenomear={(v, nome) => void executar(() => api.patch<PreferenciasLista>(`/api/preferencias/visoes/${v.id}`, { nome }))}
          aoPadrao={(v) => void executar(() => api.patch<PreferenciasLista>(`/api/preferencias/visoes/${v.id}`, { padrao: !v.padrao }))}
          aoExcluir={(v) => void executar(() => api.post<PreferenciasLista>(`/api/preferencias/visoes/${v.id}/excluir`, {})).then(() => ativa === v.id && setAtiva(null))}
        />
      )}
      {!opcoes.semColunas && <SeletorColunas tabela={tabela} ocultas={ocultas} aoAplicar={(c, o) => void salvarColunas(c, o)} />}
    </span>
  );
  return { tabela, controles, ocultas };
}

/** Fecha o painel ao clicar fora ou com Esc. */
function usePainel() {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setAberto(false);
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setAberto(false);
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => (document.removeEventListener('mousedown', fora), document.removeEventListener('keydown', esc));
  }, [aberto]);
  return { aberto, setAberto, ref };
}

function SeletorColunas({ tabela, ocultas, aoAplicar }: { tabela: RefObject<HTMLTableElement | null>; ocultas: string[]; aoAplicar: (ocultas: string[], ordem: string[]) => void }) {
  const { aberto, setAberto, ref } = usePainel();
  // colunas na ordem atual da tela (a reordenação já aplicada) e, à parte, a ordem original para "Restaurar padrão"
  const [colunas, setColunas] = useState<{ nome: string; fixa: boolean }[]>([]);
  const [originais, setOriginais] = useState<string[]>([]);
  const [rascunho, setRascunho] = useState<string[]>([]);
  const [arrastando, setArrastando] = useState<string | null>(null);
  const abrir = () => {
    const cab = tabela.current?.tHead?.rows[0];
    const cels = cab ? [...cab.cells] : [];
    const nomesCab = cels.map(textoCabecalho);
    setColunas(cels.map((th, i) => ({ nome: nomesCab[i], fixa: ehFixa(nomesCab[i], i, nomesCab) })).filter((c) => c.nome));
    setOriginais(cels.slice().sort((a, b) => Number(a.dataset.col ?? 0) - Number(b.dataset.col ?? 0)).map(textoCabecalho).filter(Boolean));
    setRascunho(ocultas);
    setAberto(!aberto);
  };
  const moveis = colunas.filter((c) => !c.fixa);
  const fixasPadrao = new Set(colunas.filter((c) => c.fixa).map((c) => c.nome));
  const mover = (nome: string, destino: number) => {
    const m = moveis.map((c) => c.nome);
    const de = m.indexOf(nome);
    if (de < 0 || destino < 0 || destino >= m.length || de === destino) return;
    m.splice(destino, 0, ...m.splice(de, 1));
    let k = 0;
    setColunas(colunas.map((c) => (c.fixa ? c : { nome: m[k++], fixa: false })));
  };
  const restaurar = () => {
    setRascunho([]);
    const fixas = new Set(colunas.filter((c) => c.fixa).map((c) => c.nome));
    setColunas(originais.map((nome) => ({ nome, fixa: fixas.has(nome) })));
  };
  return (
    <span className="ancora-filtro" ref={ref}>
      <button type="button" className="botao-secundario" aria-haspopup="true" aria-expanded={aberto} onClick={abrir} data-teste="botao-colunas">
        <Icone nome="colunas" /> Colunas{ocultas.length ? ` (${ocultas.length} oculta${ocultas.length > 1 ? 's' : ''})` : ''}
      </button>
      {aberto && (
        <div className="painel-personalizacao" role="group" aria-label="Escolher e ordenar colunas" data-teste="painel-colunas">
          <h3>Colunas visíveis e ordem</h3>
          {colunas.length === 0 && <p className="texto-secundario">A lista está vazia: as colunas aparecem quando houver registros.</p>}
          <ul className="lista-colunas">
            {colunas.map((c) => {
              const pos = moveis.findIndex((m) => m.nome === c.nome);
              return (
                <li
                  key={c.nome}
                  className={`linha-coluna${arrastando === c.nome ? ' arrastando' : ''}`}
                  draggable={!c.fixa}
                  onDragStart={(e) => (setArrastando(c.nome), e.dataTransfer.setData('text/plain', c.nome), (e.dataTransfer.effectAllowed = 'move'))}
                  onDragOver={(e) => !c.fixa && arrastando && e.preventDefault()}
                  onDrop={(e) => (e.preventDefault(), arrastando && mover(arrastando, pos), setArrastando(null))}
                  onDragEnd={() => setArrastando(null)}
                  data-teste={`linha-coluna-${c.nome}`}
                >
                  <span className={`alca-arrastar${c.fixa ? ' fixa' : ''}`} aria-hidden="true" title={c.fixa ? undefined : 'Arrastar para reordenar'}>
                    ⋮⋮
                  </span>
                  <label className="opcao">
                    <input type="checkbox" checked={c.fixa || !rascunho.includes(c.nome)} disabled={c.fixa} onChange={(e) => setRascunho(e.target.checked ? rascunho.filter((x) => x !== c.nome) : [...rascunho, c.nome])} data-teste={`coluna-${c.nome}`} />
                    <span>
                      {c.nome}
                      {c.fixa && <span className="texto-secundario"> (fixa)</span>}
                    </span>
                  </label>
                  {!c.fixa && (
                    <span className="mover-coluna">
                      <button type="button" className="botao-link" disabled={pos === 0} onClick={() => mover(c.nome, pos - 1)} aria-label={`Mover ${c.nome} para cima`} data-teste={`subir-${c.nome}`}>
                        ↑
                      </button>
                      <button type="button" className="botao-link" disabled={pos === moveis.length - 1} onClick={() => mover(c.nome, pos + 1)} aria-label={`Mover ${c.nome} para baixo`} data-teste={`descer-${c.nome}`}>
                        ↓
                      </button>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          <div className="rodape-multi">
            <button type="button" className="botao-link" onClick={restaurar} data-teste="colunas-padrao">
              Restaurar padrão
            </button>
            <button type="button" className="botao-primario pequeno" onClick={() => (aoAplicar(rascunho, ordemParaGravar(moveis.map((c) => c.nome), originais.filter((n) => !fixasPadrao.has(n)))), setAberto(false))} data-teste="colunas-aplicar">
              Aplicar
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

function SeletorVisoes({ visoes, ativa, erro, aoAplicar, aoSalvarNova, aoAtualizar, aoRenomear, aoPadrao, aoExcluir }: { visoes: Visao[]; ativa: string | null; erro: string; aoAplicar: (v: Visao) => void; aoSalvarNova: (nome: string, padrao: boolean) => void; aoAtualizar: (v: Visao) => void; aoRenomear: (v: Visao, nome: string) => void; aoPadrao: (v: Visao) => void; aoExcluir: (v: Visao) => void }) {
  const { aberto, setAberto, ref } = usePainel();
  const [modo, setModo] = useState<'lista' | 'nova' | 'gerenciar'>('lista');
  const [nome, setNome] = useState('');
  const [padrao, setPadrao] = useState(false);
  const [renomear, setRenomear] = useState<{ id: string; nome: string } | null>(null);
  const atual = visoes.find((v) => v.id === ativa);
  const abrir = () => (setModo('lista'), setNome(''), setPadrao(false), setRenomear(null), setAberto(!aberto));
  return (
    <span className="ancora-filtro" ref={ref}>
      <button type="button" className="botao-secundario" aria-haspopup="true" aria-expanded={aberto} onClick={abrir} title={atual?.nome} data-teste="botao-visoes">
        <Icone nome="filtro" /> Visão{atual ? `: ${atual.nome}` : ''} ▾
      </button>
      {aberto && (
        <div className="painel-personalizacao painel-visoes" role="group" aria-label="Minhas visões" data-teste="painel-visoes">
          <h3>Minhas visões</h3>
          {erro && (
            <p className="aviso aviso-erro" role="alert">
              {erro}
            </p>
          )}
          {modo === 'lista' && (
            <>
              {visoes.length === 0 && <p className="texto-secundario">Nenhuma visão salva nesta lista.</p>}
              {visoes.map((v) => (
                <button key={v.id} type="button" className={`item-visao${v.id === ativa ? ' ativa' : ''}`} onClick={() => (aoAplicar(v), setAberto(false))} data-teste={`visao-${v.nome}`}>
                  <span>{v.nome}</span>
                  {v.padrao && <span className="texto-secundario">padrão ★</span>}
                </button>
              ))}
              <div className="acoes-visoes">
                <button type="button" className="botao-link" onClick={() => setModo('nova')} data-teste="nova-visao">
                  Salvar filtros atuais como nova visão…
                </button>
                {atual && (
                  <button type="button" className="botao-link" onClick={() => (aoAtualizar(atual), setAberto(false))} data-teste="atualizar-visao">
                    Atualizar “{atual.nome}” com os filtros atuais
                  </button>
                )}
                {visoes.length > 0 && (
                  <button type="button" className="botao-link" onClick={() => setModo('gerenciar')} data-teste="gerenciar-visoes">
                    Gerenciar visões (renomear, excluir, padrão)
                  </button>
                )}
              </div>
            </>
          )}
          {modo === 'nova' && (
            <form
              className="form-visao"
              onSubmit={(e) => {
                e.preventDefault();
                if (!nome.trim()) return;
                aoSalvarNova(nome, padrao);
                setAberto(false);
              }}
            >
              <label>
                Nome da visão
                <input value={nome} onChange={(e) => setNome(e.target.value)} maxLength={80} autoFocus data-teste="nome-visao" />
              </label>
              <label className="opcao">
                <input type="checkbox" checked={padrao} onChange={(e) => setPadrao(e.target.checked)} data-teste="padrao-visao" /> Abrir a lista com esta visão (padrão)
              </label>
              <div className="rodape-multi">
                <button type="button" className="botao-link" onClick={() => setModo('lista')}>
                  Voltar
                </button>
                <button type="submit" className="botao-primario pequeno" disabled={!nome.trim()} data-teste="salvar-visao">
                  Salvar visão
                </button>
              </div>
            </form>
          )}
          {modo === 'gerenciar' && (
            <ul className="gerenciar-visoes">
              {visoes.map((v) => (
                <li key={v.id} data-teste={`gerenciar-${v.nome}`}>
                  {renomear?.id === v.id ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (renomear.nome.trim()) aoRenomear(v, renomear.nome);
                        setRenomear(null);
                      }}
                    >
                      <input value={renomear.nome} onChange={(e) => setRenomear({ id: v.id, nome: e.target.value })} maxLength={80} autoFocus aria-label={`Novo nome de ${v.nome}`} data-teste="renomear-visao-nome" />
                      <button type="submit" className="botao-link">
                        OK
                      </button>
                    </form>
                  ) : (
                    <span className="nome-visao">{v.nome}</span>
                  )}
                  <span className="espaco" />
                  <button type="button" className="botao-link" onClick={() => aoPadrao(v)} aria-pressed={v.padrao} title={v.padrao ? 'Deixar de ser a padrão' : 'Abrir a lista com esta visão'} data-teste={`padrao-${v.nome}`}>
                    {v.padrao ? '★ padrão' : '☆'}
                  </button>
                  <button type="button" className="botao-link" onClick={() => setRenomear({ id: v.id, nome: v.nome })} data-teste={`renomear-${v.nome}`}>
                    Renomear
                  </button>
                  <button type="button" className="botao-link" onClick={() => aoExcluir(v)} data-teste={`excluir-${v.nome}`}>
                    Excluir
                  </button>
                </li>
              ))}
              <li>
                <button type="button" className="botao-link" onClick={() => setModo('lista')}>
                  Voltar
                </button>
              </li>
            </ul>
          )}
          <p className="nota-visoes">Visão = busca, filtros, ordenação e colunas (quais e em que ordem).</p>
        </div>
      )}
    </span>
  );
}

/** Para telas sem barra de filtros: os controles alinhados à direita, acima da tabela. */
export function LinhaPersonalizacao({ children }: { children: ReactNode }) {
  return <div className="linha-personalizacao">{children}</div>;
}
